import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useApi } from '@gravitate-js/the-armory'
import { message } from 'antd'
import { getAPIURL } from '../../utils/api'
import type { TaxOverviewRow, BolTaxTransaction, RecalculateTaxResponse, MatchDetailsWithTax } from './types/tax'
import type { MatchDetailResponse } from './useMatchDetail'

export function useMatchTaxActions() {
  const queryClient = useQueryClient()
  const api = useApi({ baseURLOverride: getAPIURL() })

  const invalidateMatchQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ['post', '/supplier_rec/match/overview'],
    })
    queryClient.invalidateQueries({
      queryKey: ['matchOverview'],
    })
  }

  const useSetTaxTransactionOrder = () => {
    return {
      mutateAsync: async (params: {
        match_id: string
        source: 'invoice' | 'bol'
        source_id: string
        tax_ids: string[]
      }) => {
        invalidateMatchQueries()
        return Promise.resolve({ success: true })
      },
      isLoading: false,
      error: null,
    }
  }

  const extractOrderNumbers = (matchDetails: MatchDetailResponse): number[] => {
    const orderNumbers = new Set<number>()

    matchDetails?.document_detail_rows?.forEach((tax) => {
      if (tax.order_number) {
        orderNumbers.add(tax.order_number)
      }
    })

    return Array.from(orderNumbers)
  }

  const canRecalculateTaxes = (matchDetails: MatchDetailResponse): boolean => {
    return (matchDetails?.bol_tax_transactions?.length || 0) > 0
  }

  const mapOverviewRowsToBolTaxTransactions = (
    overviewRows: TaxOverviewRow[],
    originalTransactions: BolTaxTransaction[]
  ): BolTaxTransaction[] => {
    if (!overviewRows || !originalTransactions) return originalTransactions

    return originalTransactions.map((originalTx) => {
      // Extract order number from BOL number for matching
      const orderNumber = originalTx.bol_number?.split('-')[0]

      const matchingRow = overviewRows.find((row) => {
        const orderMatches = row.order_number === orderNumber
        const typeMatches = row.tax_type === originalTx.tax_type
        const jurisdictionMatches = row.jurisdiction === originalTx.tax_authority

        return orderMatches && typeMatches && jurisdictionMatches
      })

      if (matchingRow) {
        return {
          ...originalTx,
          rate: matchingRow.rate,
          amount: matchingRow.amount,
          total: matchingRow.value, // Map 'value' to 'total'
          tax_quantity: matchingRow.tax_quantity,
          sales_tax_base_amount: matchingRow.sales_tax_base_amount,
          rate_description: matchingRow.rate_description,
          _isRecalculated: true,
          _recalculatedAt: new Date().toISOString(),
        }
      }

      return originalTx
    })
  }

  const useRecalculateMatch = () => {
    return useMutation({
      mutationFn: async (params: { match_id: string; matchDetails: MatchDetailResponse }) => {
        const { matchDetails } = params

        if (!canRecalculateTaxes(matchDetails)) {
          throw new Error('No BOL tax transactions found - recalculation not available')
        }

        const orderNumbers = extractOrderNumbers(matchDetails)

        if (orderNumbers.length === 0) {
          throw new Error('No order numbers found in BOL tax transactions')
        }

        const recalcPromises = orderNumbers.map((order_number) =>
          api.post<RecalculateTaxResponse>('taxes/recalc', { order_number })
        )

        const results = await Promise.all(recalcPromises)

        const allOverviewRows = results.flatMap((result) => result.overview_rows || [])

        return {
          success: true,
          orderNumbers,
          results,
          totalRecalculated: orderNumbers.length,
          overviewRows: allOverviewRows,
        }
      },
      onMutate: async (request) => {
        await queryClient.cancelQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        const previousData = queryClient.getQueriesData({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        const orderNumbers = extractOrderNumbers(request.matchDetails)

        queryClient.setQueriesData({ queryKey: ['post', '/supplier_rec/match/detail'] }, (oldData: unknown) => {
          if (!oldData || typeof oldData !== 'object') return oldData

          return {
            ...oldData,
            _isRecalculating: true,
            _recalculatingOrderNumbers: orderNumbers,
          }
        })

        return { previousData, orderNumbers }
      },
      onSuccess: (response, request, context) => {
        if (response.overviewRows && response.overviewRows.length > 0) {
          queryClient.setQueriesData({ queryKey: ['post', '/supplier_rec/match/detail'] }, (oldData: unknown) => {
            if (!oldData || typeof oldData !== 'object') return oldData

            const typedOldData = oldData as MatchDetailsWithTax

            const updatedBolTaxTransactions = mapOverviewRowsToBolTaxTransactions(
              response.overviewRows,
              typedOldData.bol_tax_transactions || []
            )

            const updatedData: MatchDetailsWithTax = {
              ...typedOldData,
              bol_tax_transactions: updatedBolTaxTransactions,
              _isRecalculating: false,
              _recalculatingOrderNumbers: undefined,
              _lastRecalculatedAt: Date.now(),
            }

            return updatedData
          })
        }

        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })
        invalidateMatchQueries()

        // Show success notification
        const orderCount = response.orderNumbers?.length || 0
        message.success(`Tax recalculation completed successfully for ${orderCount} order${orderCount > 1 ? 's' : ''}`)
      },
      onError: (error, request, context) => {
        if (context?.previousData && context.previousData.length > 0) {
          context.previousData.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey, data)
          })
        }
      },
    })
  }

  return {
    useSetTaxTransactionOrder,
    useRecalculateMatch,
    extractOrderNumbers,
    canRecalculateTaxes,
    invalidateMatchQueries,
    mapOverviewRowsToBolTaxTransactions,
  }
}
